import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { getActiveProductsTypeCodes } from 'pages/dashboard/components/Progress/selectors';
import PermissionSection from 'components/permission/PermissionSection';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import { CommunicationIcon, UserAddIcon } from 'components/icons';
import { hasPermission } from 'pages/dashboard/selectors';
import { useAppDispatch, useAppSelector } from 'hooks';
import HideComponent from 'components/HideComponent';
import { ProductTypeCodes } from 'entities/progress';
import { Permissions } from 'entities/dashboard';
import { Input } from 'components/Inputs';
import Button from 'components/Button';
import Modal from 'components/Modal';
import {
  setInvitedAndWasInviteState,
  setInviteIsOpen,
} from 'components/InvitePersons/invitePersonsSlice';

import { getIsApiAccountingOpen, getApiAccount } from './selectors';
import { setIsApiAccountingOpen } from './accountingSlice';

const ApiAccountingModal = () => {
  const { t } = useTranslation('integration');
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(getIsApiAccountingOpen);
  const account = useAppSelector(getApiAccount);
  const activeProductTypes = useAppSelector(getActiveProductsTypeCodes);
  const invitePermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.invite),
  );

  const { register, reset } = useForm<FieldValues>({
    defaultValues: account || {},
  });

  useEffect(() => {
    reset(account || {});
  }, [account, reset]);

  const closeModal = () => {
    dispatch(setIsApiAccountingOpen(false));
  };

  const handleInvite = () => {
    dispatch(setInvitedAndWasInviteState());
    dispatch(setInviteIsOpen(true));
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      maxWidth="50rem"
      title={
        <div className="flex flex-1 items-center justify-between">
          <span>{t('api.title')}</span>
          <div className="flex items-center">
            <HideComponent show={invitePermissions.view}>
              <PermissionSection
                permission={Permissions.invite}
                showPopover
                edit
              >
                <Button
                  variant="link"
                  paddingClass="px-4"
                  className="ml-3 mr-[14px]"
                  size="medium"
                  onClick={handleInvite}
                  disabled={!invitePermissions.edit}
                >
                  {t('dashboard:invite')}
                  <UserAddIcon className="ml-2" />
                </Button>
              </PermissionSection>
            </HideComponent>
          </div>
        </div>
      }
      icon={<CommunicationIcon className="w-6 h-6 text-gray-500" />}
      zIndex={25}
    >
      <div className="p-6">
        <div className="flex items-end mb-6">
          <Input
            className="w-full"
            name="applicationId"
            label={t('api.clientId')}
            info={t('api.clientIdInfo')}
            register={register}
            readOnly
          />
          <CopyToClipboardButton value={account.applicationId || ''} />
        </div>
        <div className="flex items-end mb-6">
          <Input
            className="w-full"
            name="scope"
            label={t('api.scope')}
            info={t('api.scopeInfo')}
            register={register}
            readOnly
          />
          <CopyToClipboardButton value={account.scope || ''} />
        </div>
        <div className="flex items-end mb-6">
          <Input
            className="w-full"
            name="acessTokenURL"
            label={t('api.accessTokenURL')}
            info={t('api.accessTokenURLInfo')}
            register={register}
            readOnly
          />
          <CopyToClipboardButton value={account.acessTokenURL || ''} />
        </div>
        <div className="flex items-end mb-6">
          <Input
            className="w-full"
            name="payableKey"
            label={t('api.payableKey')}
            info={t('api.payableKeyInfo')}
            type="password"
            register={register}
            hideValue={false}
            readOnly={true}
          />
          <CopyToClipboardButton value={account.payableKey || ''} />
        </div>
        {activeProductTypes.includes(ProductTypeCodes.services) && (
          <div className="flex items-end mb-6">
            <Input
              className="w-full"
              name="receivableKey"
              label={t('api.receivableKey')}
              info={t('api.receivableKeyInfo')}
              type="password"
              register={register}
              hideValue={false}
              readOnly={true}
            />
            <CopyToClipboardButton value={account.receivableKey || ''} />
          </div>
        )}
        <div className="flex items-end mb-6">
          <Input
            className="w-full"
            name="partyKey"
            label={t('api.partyKey')}
            info={t('api.partyKeyInfo')}
            type="password"
            register={register}
            hideValue={false}
            readOnly={true}
          />
          <CopyToClipboardButton value={account.partyKey || ''} />
        </div>
      </div>
    </Modal>
  );
};

export default ApiAccountingModal;
